import { Dictionary, includes, isEmpty, omitBy } from 'lodash';
import React, { useMemo } from 'react';

import { BulkCaseApprovalModal } from 'components/ActionsMenu/BulkCaseApprovalModal';
import { DialogWithCloseButton } from 'components/atoms/Dialog/DialogWithCloseButton';
import JobModal from 'components/JobModal';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import SaveCohortModal from 'components/SearchFilters/SaveCohortModal';
import { CreateAnnotationAssignmentDialog } from 'components/StudyDashboard/AnnotationAssignment/CreateAnnotationAssignmentDialog';
import ApproveResultsForm from 'components/StudyDashboard/ProceduresPage/Actions/ApproveResults/ApproveResultsForm';
import AssignNeighborhoodNamesForm from 'components/StudyDashboard/ProceduresPage/Actions/AssignNeighborhoodNamesForm';
import { applyMarkerNamingCasesParams } from 'components/StudyDashboard/ProceduresPage/Actions/BulkMultiplexMarkerNamingDialog/markerNamingDialogParams';
import { BulkMultiplexNormalizationDialog } from 'components/StudyDashboard/ProceduresPage/Actions/BulkMultiplexNormalizationDialog';
import { BulkSlideTaggingDialog } from 'components/StudyDashboard/ProceduresPage/Actions/BulkSlideTaggingDialog';
import { DefineMultiplexPanelsDialog } from 'components/StudyDashboard/ProceduresPage/Actions/DefineMultiplexPanelsDialog';
import { CohortQueryObject } from 'interfaces/cohort';
import { JobType } from 'interfaces/job';
import { useCasesParams } from 'utils/useCasesParams';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { ActionFormDirtyProvider, createActionFormCloseHandler, useActionFormDirtyStore } from './actionFormDirtyState';
import { ActionDialogType } from './constants';
import { useActiveActionDialogType, useAdditionalParamsDialog } from './useActionDialogParams';

export { ActionDialogType } from './constants';
export { useActiveActionDialogType, useAdditionalParamsDialog } from './useActionDialogParams';

export const actionModalToJobType: Dictionary<JobType> = {
  [ActionDialogType.PostProcessingAndCalculateFeatures]: JobType.PostProcessingAndCalculateFeatures,
  [ActionDialogType.PostProcessingAndCalculateFeaturesSubMenu]: JobType.PostProcessingAndCalculateFeatures,
  [ActionDialogType.SlideRegistrations]: JobType.SlideRegistrations,
  [ActionDialogType.MultiplexCellSegmentation]: JobType.MultiplexCellSegmentation,
  [ActionDialogType.MultiplexBinaryClassifier]: JobType.BinaryClassifier,
  [ActionDialogType.CloudOptimizedSlides]: JobType.CloudOptimizedSlides,
  [ActionDialogType.TileGeneration]: JobType.TileGeneration,
  [ActionDialogType.MultiplexHistogram]: JobType.MultiplexHistogram,
  [ActionDialogType.MultiplexNormalization]: JobType.MultiplexNormalization,
  [ActionDialogType.MultiplexThreshold]: JobType.MultiplexThreshold,
  [ActionDialogType.MultiplexCellTyping]: JobType.MultiplexCellTyping,
  [ActionDialogType.RnaProbePeakDetection]: JobType.MultiplexPeakDetection,
  [ActionDialogType.MultiplexBlobSegmentation]: JobType.MultiplexBlobSegmentation,
  [ActionDialogType.DetectTmaCores]: JobType.DetectTmaCores,
  [ActionDialogType.CropTma]: JobType.CropTma,
  [ActionDialogType.Inference]: JobType.Inference,
  [ActionDialogType.ExecuteStudyAlgorithm]: JobType.NucleaiExecutor,
  [ActionDialogType.NeighborhoodClusters]: JobType.NeighborhoodClusters,
  [ActionDialogType.CellSegmentation]: JobType.CellSegmentation,
  [ActionDialogType.CellQuantification]: JobType.CellQuantification,
  [ActionDialogType.MifMarkerUnsupervisedAnalysis]: JobType.MifMarkerUnsupervisedAnalysis,
  [ActionDialogType.QaAnalysis]: JobType.QaAnalysis,
  [ActionDialogType.QcAnalysis]: JobType.QcAnalysis,
  [ActionDialogType.StudyReport]: JobType.StudyReport,
  [ActionDialogType.MultiplexTissueSegmentationMask]: JobType.MultiplexTissueSegmentationMask,
  [ActionDialogType.ManualPostIngestPipeline]: JobType.ManualPostIngestPipeline,
  [ActionDialogType.BrightfieldTissueSegmentationMask]: JobType.BrightfieldTissueSegmentationMask,
  [ActionDialogType.PrepareCustomerResults]: JobType.PrepareCustomerResults,
};

export const ActionDialog: React.FC = () => {
  const { queryParams, convertToQueryObject } = useEncodedFilters();
  const [currentActionItemType, setCurrentActionItemType] = useActiveActionDialogType();
  const [additionalParams, setAdditionalParams] = useAdditionalParamsDialog();
  const jobTypeForActionDialog = currentActionItemType && actionModalToJobType[currentActionItemType];

  const { casesParams } = useCasesParams();
  const markerNamingCasesParams = useMemo(
    () =>
      currentActionItemType === ActionDialogType.BulkMultiplexMarkerNaming
        ? applyMarkerNamingCasesParams(casesParams, additionalParams)
        : casesParams,
    [additionalParams, casesParams, currentActionItemType]
  );
  const cohortQueryObject = useMemo(() => convertToQueryObject(queryParams), [queryParams]);
  const cohortQueryObjectWithSelection: CohortQueryObject = {
    ...cohortQueryObject,
    fromSelection: true,
    slidesMode: casesParams.slidesMode,
    ...omitBy(
      {
        caseIdsToInclude: casesParams.caseIdsToInclude,
        caseIdsToExclude: casesParams.caseIdsToExclude,
        slideIdsToInclude: casesParams.slideIdsToInclude,
        slideIdsToExclude: casesParams.slideIdsToExclude,
      },
      isEmpty
    ),
  };

  const confirmWithModal = useConfirmation();
  const { contextValue: dirtyStateContextValue, getDirtyState } = useActionFormDirtyStore();
  const clearActionItem = createActionFormCloseHandler(getDirtyState, confirmWithModal, () => {
    setAdditionalParams(undefined);
    setCurrentActionItemType(null);
  });

  return (
    <ActionFormDirtyProvider value={dirtyStateContextValue}>
      {currentActionItemType &&
        (jobTypeForActionDialog ? (
          <JobModal
            casesParams={casesParams}
            jobType={jobTypeForActionDialog}
            onClose={clearActionItem}
            additionalParams={additionalParams}
          />
        ) : includes([ActionDialogType.ApproveCases, ActionDialogType.DisapproveCases], currentActionItemType) ? (
          <BulkCaseApprovalModal
            onCloseModal={clearActionItem}
            internallyApproved={currentActionItemType === ActionDialogType.ApproveCases}
          />
        ) : currentActionItemType === ActionDialogType.ApproveResults ? (
          <DialogWithCloseButton header="Approve Results" onClose={clearActionItem}>
            <ApproveResultsForm onClose={clearActionItem} />
          </DialogWithCloseButton>
        ) : currentActionItemType === ActionDialogType.AssignNeighborhoodNames ? (
          <DialogWithCloseButton header="Assign Neighborhood Names" onClose={clearActionItem}>
            <AssignNeighborhoodNamesForm onClose={clearActionItem} />
          </DialogWithCloseButton>
        ) : currentActionItemType === ActionDialogType.BulkSlideTagging ? (
          <DialogWithCloseButton header="Bulk Slide Tagging" onClose={clearActionItem}>
            <BulkSlideTaggingDialog casesParams={casesParams} onClose={clearActionItem} />
          </DialogWithCloseButton>
        ) : currentActionItemType === ActionDialogType.BulkMultiplexNormalization ? (
          <DialogWithCloseButton header="Set Normalization Range" onClose={clearActionItem}>
            <BulkMultiplexNormalizationDialog casesParams={casesParams} onClose={clearActionItem} />
          </DialogWithCloseButton>
        ) : currentActionItemType === ActionDialogType.BulkMultiplexMarkerNaming ? (
          <DefineMultiplexPanelsDialog casesParams={markerNamingCasesParams} onClose={clearActionItem} />
        ) : currentActionItemType === ActionDialogType.CreateCohort ? (
          <SaveCohortModal
            isOpen={true}
            onClose={clearActionItem}
            queryObject={cohortQueryObjectWithSelection}
            onlyCohort
          />
        ) : currentActionItemType === ActionDialogType.CreateAssignment ? (
          <CreateAnnotationAssignmentDialog open onClose={clearActionItem} />
        ) : null)}
    </ActionFormDirtyProvider>
  );
};
